import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, useProcesses } from "../App";
import Layout from "../components/Layout";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { MagnifyingGlass, DownloadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import React from "react";
import { exportWithHeader } from "../utils/excelExport";

// Short code (max 4 chars) used in the compact table header for each process.
const SHORT_OVERRIDES = {
  cutting: "Cut", incoming_store: "IS", machining_p1: "M1", machining_p2: "M2",
  semi_firewall: "SFW", painting: "Pnt", silver_plating: "SP", welding: "Wld",
  outsource: "OS", firewall: "FW", dispatch: "Dsp",
};
const shortCode = (code, name) => {
  if (SHORT_OVERRIDES[code]) return SHORT_OVERRIDES[code];
  // Build a 3-letter abbreviation from the customised name (e.g. "CNC Machining" -> "CNC").
  const src = (name || code || "").trim();
  if (!src) return "?";
  const words = src.split(/[\s_]+/).filter(Boolean);
  if (words.length >= 2) return (words[0][0] + words[1][0] + (words[2]?.[0] || "")).toUpperCase().slice(0, 4);
  return src.slice(0, 4).toUpperCase();
};

function f2(v) { if (v == null || v === 0) return "-"; return typeof v === 'number' ? v.toFixed(2) : v; }
function fmtVal(v) {
  if (!v || v === 0) return "-";
  if (v >= 10000000) return (v / 10000000).toFixed(2) + " Cr";
  if (v >= 100000) return (v / 100000).toFixed(2) + " L";
  if (v >= 1000) return (v / 1000).toFixed(1) + " K";
  return v.toFixed(2);
}
function ybg(v) { return v > 0 ? 'bg-[#FFFDE7]' : ''; }

export default function Inventory() {
  // Pull live tenant processes from Customization (sequence-ordered).
  const { processes } = useProcesses();
  // Fallback to defaults only while the live list is loading.
  const STAGE_OPS = (processes && processes.length
    ? processes
    : [
        { code: "cutting", name: "Cutting" }, { code: "incoming_store", name: "Incoming Store" },
        { code: "machining_p1", name: "Machining P1" }, { code: "machining_p2", name: "Machining P2" },
        { code: "semi_firewall", name: "Semi-Firewall" }, { code: "painting", name: "Painting" },
        { code: "silver_plating", name: "Silver Plating" }, { code: "welding", name: "Welding" },
        { code: "firewall", name: "Firewall" }, { code: "dispatch", name: "Dispatch" },
      ]
  ).map(p => ({ key: p.code, label: p.name || p.code, short: shortCode(p.code, p.name) }));

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const fetchData = useCallback(async (silent = false) => {
    try {
      const res = await axios.get(`${API}/inventory/valuation`);
      setData(res.data);
    } catch { if (!silent) toast.error("Failed to load inventory"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(() => fetchData(true), 20000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const sq = search.toLowerCase().trim();
  const filtered = sq ? data.filter(r =>
    (r.part_no || "").toLowerCase().includes(sq) ||
    (r.customer || "").toLowerCase().includes(sq) ||
    (r.description || "").toLowerCase().includes(sq)
  ) : data;

  const handleExport = () => {
    const rows = filtered.map(r => {
      const row = { "Part No": r.part_no, "Customer": r.customer, "Part Rate": r.part_rate, "RM Value/pc": r.rm_value_per_pc };
      for (const op of STAGE_OPS) {
        const s = (r.stages || {})[op.key] || {};
        row[`${op.label} Cum.Rate`] = s.cumulative_rate || 0;
        row[`${op.label} Quantity`] = s.bal_qty || 0;
        row[`${op.label} Value`] = s.stage_value || 0;
      }
      row["Total Inventory Value"] = r.total_inventory_value || 0;
      return row;
    });
    exportWithHeader([{ name: "Inventory", data: rows }], `Inventory_Valuation_${new Date().toISOString().slice(0,10)}`, "Inventory Valuation — All Stages");
    toast.success("Exported!");
  };

  const grandTotal = filtered.reduce((s, r) => s + (r.total_inventory_value || 0), 0);

  return (
    <Layout>
      <div className="p-6 space-y-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-black tracking-tight text-[#0F172A]" style={{ fontFamily: 'Chivo' }}>Inventory</h1>
            <p className="text-sm text-[#64748B] mt-1">Cumulative value at each operation stage — RM + progressive operation costs</p>
          </div>
          <div className="flex items-center gap-3">
            <Card className="px-5 py-3 border border-[#E2E8F0] text-center">
              <p className="text-xs text-[#64748B] uppercase font-bold">Total Inventory Value</p>
              <p className="text-2xl font-black text-[#1D2951] font-mono mt-1">{fmtVal(grandTotal)}</p>
            </Card>
            <Button onClick={handleExport} className="bg-[#1D2951] hover:bg-[#2A3B73] text-white min-h-[40px]" data-testid="export-inventory-btn">
              <DownloadSimple size={16} className="mr-1" /> Export
            </Button>
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
          <Input placeholder="Search by Part No, Customer, Description..." value={search}
            onChange={e => setSearch(e.target.value)} className="pl-10 min-h-[44px]" data-testid="inventory-search" />
        </div>

        {/* Table */}
        <Card className="border border-[#E2E8F0] overflow-hidden rounded-xl">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="inventory-table">
              <thead>
                <tr className="bg-[#1D2951] text-white">
                  <th className="px-3 py-3 text-left text-xs font-bold sticky left-0 bg-[#1D2951] z-10" rowSpan="2">Part No</th>
                  <th className="px-3 py-3 text-right text-xs font-bold" rowSpan="2">RM Val/pc</th>
                  {STAGE_OPS.map(op => (
                    <th key={op.key} className="px-1 py-2 text-center text-[10px] font-bold border-l border-[#2A3B73]" colSpan="2">{op.short}</th>
                  ))}
                  <th className="px-3 py-3 text-right text-xs font-bold border-l border-[#2A3B73]" rowSpan="2">Total Value</th>
                </tr>
                <tr className="bg-[#2A3B73] text-white">
                  {STAGE_OPS.map(op => (
                    <React.Fragment key={op.key}>
                      <th className="px-1 py-1.5 text-right text-[10px] border-l border-[#3B4D84]">Quantity</th>
                      <th className="px-1 py-1.5 text-right text-[10px]">Value</th>
                    </React.Fragment>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-[#E2E8F0]">
                {loading ? (
                  <tr><td colSpan={3 + STAGE_OPS.length * 2} className="text-center py-8 text-[#64748B]">Loading...</td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={3 + STAGE_OPS.length * 2} className="text-center py-8 text-[#64748B]">No parts found</td></tr>
                ) : filtered.map((r, idx) => (
                  <tr key={r.part_id} className="hover:bg-[#F8FAFC]" data-testid={`inv-row-${idx}`}>
                    <td className="px-3 py-2 font-mono font-bold text-[#1D2951] text-xs sticky left-0 bg-white z-10">{r.part_no}</td>
                    <td className={`px-3 py-2 text-right font-mono text-xs ${ybg(r.rm_value_per_pc)}`}>{f2(r.rm_value_per_pc)}</td>
                    {STAGE_OPS.map(op => {
                      const s = (r.stages || {})[op.key] || {};
                      return (
                        <React.Fragment key={op.key}>
                          <td className={`px-1 py-2 text-right font-mono text-xs border-l border-[#F1F5F9] ${ybg(s.bal_qty)}`}>{f2(s.bal_qty)}</td>
                          <td className={`px-1 py-2 text-right font-mono text-xs font-bold ${s.stage_value > 0 ? 'text-[#059669] bg-[#FFFDE7]' : ''}`}>{fmtVal(s.stage_value)}</td>
                        </React.Fragment>
                      );
                    })}
                    <td className={`px-3 py-2 text-right font-mono text-xs font-black border-l border-[#E2E8F0] ${r.total_inventory_value > 0 ? 'text-[#1D2951] bg-[#FFFDE7]' : ''}`}>
                      {fmtVal(r.total_inventory_value)}
                    </td>
                  </tr>
                ))}
              </tbody>
              {filtered.length > 0 && (
                <tfoot>
                  <tr className="bg-[#1D2951] text-white font-bold">
                    <td className="px-3 py-3 sticky left-0 bg-[#1D2951] z-10">TOTAL ({filtered.length})</td>
                    <td className="px-3 py-3"></td>
                    {STAGE_OPS.map(op => {
                      const stageTotal = filtered.reduce((s, r) => s + ((r.stages || {})[op.key]?.stage_value || 0), 0);
                      return (
                        <React.Fragment key={op.key}>
                          <td className="px-1 py-3 border-l border-[#2A3B73]"></td>
                          <td className="px-1 py-3 text-right font-mono text-xs">{fmtVal(stageTotal)}</td>
                        </React.Fragment>
                      );
                    })}
                    <td className="px-3 py-3 text-right font-mono border-l border-[#2A3B73]">{fmtVal(grandTotal)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </Card>
      </div>
    </Layout>
  );
}
